import { z } from 'zod';

import type {
  CreateRoleRequest,
  Role,
  UpdateRoleRequest,
} from '@nucleus-commerce/api-client/admin';

import { isRolePermission, type RolePermission } from './role-rules';

export const roleFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, { error: 'team:roles.validation.nameRequired' })
    .max(80, { error: 'team:roles.validation.nameLength' }),
  key: z
    .string()
    .trim()
    .min(1, { error: 'team:roles.validation.keyRequired' })
    .max(60, { error: 'team:roles.validation.keyLength' })
    .regex(/^[a-z][a-z0-9_]*$/, { error: 'team:roles.validation.keyFormat' }),
  permissions: z
    .array(z.string())
    .min(1, { error: 'team:roles.validation.permissionsRequired' })
    .max(42),
});

export type RoleFormValues = z.infer<typeof roleFormSchema>;

export function emptyRoleForm(): RoleFormValues {
  return { name: '', key: '', permissions: [] };
}

export function fromRole(role: Role): RoleFormValues {
  return { name: role.name, key: role.key, permissions: role.permissions };
}

const permissionsOf = (values: RoleFormValues): RolePermission[] =>
  values.permissions.filter(isRolePermission);

export function toRoleCreate(values: RoleFormValues): CreateRoleRequest {
  return { key: values.key, name: values.name, permissions: permissionsOf(values) };
}

/** The key is fixed once created; members refer to it. */
export function toRoleUpdate(values: RoleFormValues): UpdateRoleRequest {
  return { name: values.name, permissions: permissionsOf(values) };
}
